import { IonCol, IonGrid, IonRow } from "@ionic/react";
import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ActionIconButton from "@components/buttons/ActionIconButton";
import TertiaryButton from "@components/buttons/TertiaryButton";
import styles from "@components/ConsentForm.module.css";
import DateStamp from "@components/dates/DateStamp";
import DeleteConfirmationAlert from "@components/DeleteConfirmationAlert";
import Nbsp from "@components/Nbsp";
import Icon from "@components/text/Icon";
import TextWithIcon from "@components/text/TextWithIcon";
import ButtonLink from "@components/typography/ButtonLink";
import useLazyQueryWithPromise from "@hooks/useLazyQueryWithPromise";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyExpert from "@hooks/useMyExpert";
import useOpener, { Opener } from "@hooks/useOpener";
import {
  FaxAttemptStatusEnum,
  GetSignedDocumentAttachmentUrlDocument,
  GetSignedDocumentAttachmentUrlQuery,
  GetSignedDocumentAttachmentUrlQueryVariables,
  GetSignedDocumentDocument,
  GetSignedDocumentQuery,
  GetSignedDocumentQueryVariables,
  SignedDocumentFragment as SignedDocument,
  SignedDocumentRevokeDocument,
  SignedDocumentRevokeMutation,
  SignedDocumentRevokeMutationVariables,
  SignedDocumentStatusEnum
} from "@typing/Generated";
import { downloadFile } from "@utils/downloadUtils";
import { mostRecentDate } from "@utils/modelUtils";

type Props = {
  consentForm: SignedDocument;
  faxOpener: Opener;
  setCurrentFaxFormId: (id: string) => void;
};

const ConsentForm = ({ consentForm: consentFormArgument, faxOpener, setCurrentFaxFormId }: Props) => {
  const [consentForm, setConsentForm] = useState(consentFormArgument);
  const revokeConfirmationOpener = useOpener();

  const myExpert = useMyExpert();

  const intervalRef = useRef<NodeJS.Timeout | undefined>();
  const intl = useIntl();

  const getSignedDocument = useLazyQueryWithPromise<GetSignedDocumentQuery, GetSignedDocumentQueryVariables>(
    GetSignedDocumentDocument
  );

  const mostRecentFaxAttempt = useMemo(
    () => mostRecentDate(consentForm.faxAttempts, faxAttempt => faxAttempt.createdAt),
    [consentForm]
  );

  useEffect(() => {
    if (consentForm.attachmentScanning && !intervalRef.current) {
      intervalRef.current = setInterval(() => {
        getSignedDocument({ fetchPolicy: "network-only", variables: { id: consentForm.id } }).then(response => {
          if (!response.data.signedDocument.attachmentScanning) {
            setConsentForm({
              ...consentForm,
              ...response.data.signedDocument
            });
          }
        });
      }, 5000);
    } else if (!consentForm.attachmentScanning && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = undefined;
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = undefined;
      }
    };
  }, [consentForm, getSignedDocument]);

  const [signedDocumentRevoke] = useMutationWithValidation<
    SignedDocumentRevokeMutation,
    SignedDocumentRevokeMutationVariables
  >(SignedDocumentRevokeDocument, "signedDocument");

  const getAttachment = useLazyQueryWithPromise<
    GetSignedDocumentAttachmentUrlQuery,
    GetSignedDocumentAttachmentUrlQueryVariables
  >(GetSignedDocumentAttachmentUrlDocument);

  const handleDownload = useCallback(
    (event: MouseEvent<HTMLButtonElement>, consentForm: SignedDocument) => {
      event.preventDefault();
      getAttachment({ fetchPolicy: "no-cache", variables: { id: consentForm.id } }).then(response => {
        if (response.data.signedDocument.attachmentUrl) {
          downloadFile(response.data.signedDocument.attachmentUrl, consentForm.attachmentFilename as string);
        }
      });
    },
    [getAttachment]
  );

  const handleStartRevoke = useCallback(() => {
    revokeConfirmationOpener.open();
  }, [revokeConfirmationOpener]);

  const handleRevoke = useCallback(() => {
    signedDocumentRevoke({ id: consentForm.id }).then(res => {
      if (res?.signedDocumentRevoke?.signedDocument) {
        setConsentForm(res.signedDocumentRevoke.signedDocument);
      }
    });
  }, [consentForm.id, signedDocumentRevoke]);

  return (
    <div className={styles.signedConsentForm}>
      <IonGrid>
        <IonRow className="ion-align-items-center">
          <IonCol>
            {consentForm.attachmentUrl ? (
              <ButtonLink onClick={event => handleDownload(event, consentForm)}>
                <FormattedMessage
                  id={`models.consentForm.status.${consentForm.status}.headline`}
                  values={{ name: consentForm.documentFor }}
                />
              </ButtonLink>
            ) : (
              <>
                {consentForm.attachmentScanning ? (
                  <TextWithIcon
                    icon="spinner"
                    iconFirst
                    text={intl.formatMessage({ id: "models.consentForm.documentScanning" })}
                  />
                ) : (
                  <FormattedMessage
                    id={`models.consentForm.status.${consentForm.status}.headline`}
                    values={{ name: consentForm.documentFor }}
                  />
                )}
              </>
            )}
          </IonCol>
          <IonCol className="ion-hide-sm-down">
            {consentForm.status !== SignedDocumentStatusEnum.EMBED_CREATED && (
              <FormattedMessage
                id={`models.consentForm.status.${consentForm.status}.description`}
                values={{
                  recipientEmailAddress: consentForm.recipientEmailAddress,
                  recipientName: consentForm.recipientName
                }}
              />
            )}
          </IonCol>
          <IonCol className="ion-hide-sm-down">
            <DateStamp value={consentForm.createdAt} />
            {consentForm.status === SignedDocumentStatusEnum.REVOKED && (
              <>
                <Nbsp />
                <FormattedMessage id="dictionary.to" />
                <Nbsp />
                <DateStamp value={consentForm.revokedAt} />
              </>
            )}
          </IonCol>
          <IonCol className="align-right">
            {!myExpert && consentForm.status === SignedDocumentStatusEnum.COMPLETE && (
              <ActionIconButton
                ariaLabelKey="accessibility.ariaLabels.delete"
                icon="xmark"
                onClick={handleStartRevoke}
              />
            )}
          </IonCol>
          {myExpert && !consentForm.revokedAt && (
            <IonCol size="auto">
              {mostRecentFaxAttempt && (
                <>
                  <FormattedMessage id="models.consentForm.fax.status" />
                  <FormattedMessage id={`models.consentForm.fax.statusCodes.${mostRecentFaxAttempt.status}`} />
                  {(mostRecentFaxAttempt.status === FaxAttemptStatusEnum.PENDING ||
                    mostRecentFaxAttempt.status === FaxAttemptStatusEnum.TRANSMITTING) && (
                    <Icon icon="spinner" size="small" spacingStart />
                  )}
                </>
              )}
              <TertiaryButton
                icon="print"
                onClick={() => {
                  setCurrentFaxFormId(consentForm.id);
                  faxOpener.open();
                }}
                textKey="models.consentForm.fax.fax"
              />
            </IonCol>
          )}
        </IonRow>
      </IonGrid>
      <DeleteConfirmationAlert
        deleteKey="models.consentForm.revokeConsent"
        headerKey="models.consentForm.revokeHeader"
        messageKey="models.consentForm.reallyRevoke"
        onDelete={handleRevoke}
        opener={revokeConfirmationOpener}
      />
    </div>
  );
};

export default ConsentForm;
